import Decimal from 'decimal.js'

/**
 * Money is a STRING, never a number.
 *
 * DRF serialises `DecimalField` to a string ("512.00") by default, and the
 * portfolio audit (MM_PORTFOLIO.md, finding 9) already records components that
 * fail to reconcile once values pass through a float. IEEE-754 cannot hold
 * 0.1 exactly, so `0.1 + 0.2 !== 0.3` — over a holdings list that becomes a
 * visible rupee discrepancy in a total.
 *
 * The brand makes the string nominal: a bare string will not satisfy `Money`,
 * so a raw API field cannot silently flow into a money slot untyped.
 */
export type Money = string & { readonly __brand: 'Money' }

/** Wraps a backend decimal string. Throws on anything non-numeric. */
export const money = (value: string | number): Money => {
  const raw = typeof value === 'number' ? String(value) : value.trim()
  if (raw === '' || !Number.isFinite(Number(raw))) {
    throw new TypeError(`Not a money value: ${JSON.stringify(value)}`)
  }
  return raw as Money
}

export const ZERO = money('0')

const dec = (m: Money) => new Decimal(m)
const wrap = (d: Decimal): Money => d.toFixed() as Money

export const add = (a: Money, b: Money): Money => wrap(dec(a).plus(dec(b)))
export const sub = (a: Money, b: Money): Money => wrap(dec(a).minus(dec(b)))
export const mul = (a: Money, factor: number | string): Money => wrap(dec(a).times(factor))
export const div = (a: Money, divisor: number | string): Money => wrap(dec(a).dividedBy(divisor))
export const sum = (values: readonly Money[]): Money =>
  values.reduce<Money>((acc, v) => add(acc, v), ZERO)

/** -1 | 0 | 1 — the sign a Change component renders as ▼ · — · ▲ */
export const cmp = (a: Money, b: Money): -1 | 0 | 1 => dec(a).comparedTo(dec(b)) as -1 | 0 | 1
export const sign = (m: Money): -1 | 0 | 1 => cmp(m, ZERO)
export const isZero = (m: Money): boolean => dec(m).isZero()
export const abs = (m: Money): Money => wrap(dec(m).abs())
export const neg = (m: Money): Money => wrap(dec(m).negated())

/** Percentage change from `from` to `to`, as a Money-typed percentage value. */
export const pctChange = (from: Money, to: Money): Money => {
  const base = dec(from)
  if (base.isZero()) return ZERO
  return wrap(dec(to).minus(base).dividedBy(base).times(100))
}

/**
 * Rounds a DERIVED value to the 2 dp a rupee figure or a displayed percent is
 * read at. Always returns the trailing zeros — `"500.00"`, not `"500"`.
 *
 * Two separate things make this necessary, and neither is a formatting nicety:
 *
 *  1. `Decimal.toFixed()` with no argument STRIPS trailing zeros, so a sum of
 *     two server figures (`"81748.00" + "38600.00"`) comes back `"120348"` —
 *     a different string shape from every `Money` the API serves, which is
 *     always written out to its full decimal places.
 *  2. Division does not terminate. `2000 ÷ 108000 × 100` is
 *     `"1.8518518518518518519"` — decimal.js's 20-significant-digit default,
 *     which is a leak of the arithmetic library's precision setting into a
 *     figure a member reads. A sector's "Today %" is a 2 dp number.
 *
 * ROUND_HALF_EVEN, not HALF_UP, and that choice is observable: `fees.py`
 * quantises with ROUND_HALF_UP internally, but DRF's own output layer
 * serialises with ROUND_HALF_EVEN (`502.125` leaves the server as `"502.12"`,
 * not `"502.13"`). A locally-derived figure must land where the SERVER would
 * have landed had it derived the same figure, or the two disagree by a paisa
 * on exact halves.
 *
 * Use it on genuinely client-derived aggregates only. Where the server already
 * sends the figure, display the server's own string — re-deriving it here can
 * only introduce a difference, never remove one.
 */
export const quantise = (m: Money): Money =>
  dec(m).toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toFixed(2) as Money

/**
 * Only for handing a value to a charting library, which takes numbers.
 * Never feed the result back into a calculation or a stored value.
 */
export const toChartNumber = (m: Money): number => dec(m).toNumber()
